import { apiUrl } from '../api/config';
import type { ShareLinkCreatedDto, ShareLinkDto as ShareLinkRowDto } from '@gty/common';

/**
 * The share-link contract, front-side.
 *
 * RE-EXPORTED FROM `@gty/common`, not redeclared. An earlier draft of this file
 * declared its own skinny local shapes on the "no server DTO exists yet" theory
 * `payments/cheque-actions.ts` uses — but the backend agent working the same
 * item in parallel DID publish `ShareLinkCreatedDto`/`ShareLinkDto` on the
 * shared package, so keeping a second, DIVERGENT local guess (the local
 * `ShareLinkSummaryDto` had only `{ id, expiresAt }`, missing the real
 * `documentType`/`documentId`/`fileName`/`revokedAt`/`active`/`accessCount`/
 * `lastAccessedAt` the list endpoint actually returns) would have been exactly
 * the two-copies-of-a-closed-set drift this codebase's own conventions exist to
 * avoid (see `endpoints.ts`'s header comment). `ShareLinkDto` is renamed on
 * import to keep every call site in this package unchanged.
 */
export type ShareLinkDto = ShareLinkCreatedDto;
export type ShareLinkSummaryDto = ShareLinkRowDto;

/**
 * The document families that have `/share` routes.
 *
 * Deliberately a SEPARATE type from `PdfKind` in `api/download.ts` rather than
 * an alias — and `tours` is now the member that proves why. The two unions
 * coincided exactly while there were four members each; `tours` has a `/share`
 * route (`POST /tours/:id/brochure/share`) and NO `/pdf` counterpart, because a
 * brochure is a file someone uploaded rather than a document this app renders.
 * Had these been aliased, adding it here would have silently widened the
 * download surface to a route that does not exist.
 *
 * NOT every member maps to `/<kind>/<id>/share`, either — see `sharePath` in
 * `api/endpoints.ts`, which special-cases two of them:
 * - `payments/receipts` is a path-segment PAIR, because the receipt routes live
 *   under payments — `POST /payments/receipts/:id/share` mirrors the
 *   `GET /payments/receipts/:id` the ledger already reads, so the share carries
 *   the same `PAYMENTS_RECEIPTS` permission as the screen.
 * - `tours` carries an extra `/brochure` segment, because the shared thing is a
 *   FIELD of a tour rather than a document family of its own.
 */
export type ShareDocumentKind =
  | 'invoices'
  | 'vouchers'
  | 'credit-notes'
  | 'payments/receipts'
  | 'tours';

/**
 * The URL a customer opens, assembled CLIENT-SIDE.
 *
 * The server never returns a full URL and never derives an origin from the
 * `Host` header — it hands back the bare `token` and nothing else. That is not
 * fussiness: an origin taken from a request header is attacker-controlled, and
 * the resulting "share link" would point wherever the header said. Here the
 * origin is the one the staff member is demonstrably already on.
 *
 * `apiUrl` rather than a hardcoded `/api`: the API is a PATH on this same origin
 * (see `api/config.ts` — the refresh cookie is SameSite=Strict, so app and API
 * must be same-site), and `apiUrl` is the single place that path is written.
 * The public `GET /share/:token` route is unauthenticated, so the customer
 * needs no session to open it.
 */
export function buildShareUrl(token: string): string {
  return `${window.location.origin}${apiUrl(`/share/${encodeURIComponent(token)}`)}`;
}
